import { metrics, registry } from '@geo-admin/shared/contract/api/index.ts';
import { EngineTabs, useEngine } from '../components/EngineTabs.tsx';
import { SITE_KINDS } from '@geo-admin/shared/contract/probe-values.ts';
import type { RouteResponse } from '@geo-admin/shared/contract/route.ts';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { DataTable } from '../components/data-table.tsx';
import { RateCell } from '../components/RateCell.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { SidePanel } from '../components/ui/dialog.tsx';
import { FieldError, Input, Select } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { PageTitle } from '../components/PageTitle.tsx';
import { Tabs, useBrandKind } from '../components/BrandKindTabs.tsx';
import { dateTime, relative } from '../lib/format.ts';
import { PATHS } from '../lib/paths.ts';
import { cn } from '../lib/utils.ts';

type SiteRow = RouteResponse<typeof metrics.siteMetrics>['sites'][number];
type DeletedSiteRow = RouteResponse<typeof registry.listDeletedSites>['sites'][number];
type SiteQueryRow = RouteResponse<typeof metrics.siteQueries>['queries'][number];
type SiteSampleRow = RouteResponse<typeof metrics.siteSamples>['samples'][number];
const DAYS = 30;

/** 인용 URL 을 접두사 뒤 경로로 줄여 보인다 — 같은 사이트라 host 는 정보가 없다 */
const shortUrl = (u: string) => {
  try {
    const x = new URL(u);
    return decodeURI(x.pathname + x.search) || '/';
  } catch {
    return u;
  }
};

function siteQueryColumns(showSamples: (queryId: string) => void): ColumnDef<SiteQueryRow, unknown>[] {
  return [
    {
      header: '질문',
      accessorKey: 'query',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <span className="font-medium">{row.original.query}</span>
          {row.original.deleted ? <Badge>삭제됨</Badge> : null}
        </div>
      ),
    },
    { header: '고객', accessorKey: 'brandName' },
    {
      header: '이 사이트 인용률',
      id: 'cited',
      accessorFn: (r) => r.cited.k,
      cell: ({ row }) => <RateCell rate={row.original.cited} />,
    },
    {
      header: '그중 브랜드 언급',
      id: 'withMention',
      accessorFn: (r) => r.citedWithMention,
      cell: ({ row }) => (
        <span className="tabular-nums">
          {row.original.citedWithMention}/{row.original.cited.k}
        </span>
      ),
    },
    {
      header: '마지막 인용',
      accessorKey: 'lastCitedAt',
      cell: ({ row }) => <span className="text-slate-500">{relative(row.original.lastCitedAt)}</span>,
    },
    {
      header: '',
      id: 'samples',
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation(); // 행 클릭(질문 상세로 이동)과 갈라 둔다
            showSamples(row.original.queryId);
          }}
        >
          샘플 {row.original.cited.k}
        </Button>
      ),
    },
  ];
}

const siteSampleColumns: ColumnDef<SiteSampleRow, unknown>[] = [
  {
    header: '시각',
    accessorKey: 'createdAt',
    cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{dateTime(row.original.createdAt)}</span>,
  },
  {
    header: '질문',
    accessorKey: 'query',
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.query}</div>
        <div className="text-xs text-slate-500">{row.original.brandName}</div>
      </div>
    ),
  },
  {
    header: '브랜드 언급',
    accessorKey: 'mentioned',
    cell: ({ row }) =>
      row.original.mentioned ? <Badge tone="green">언급</Badge> : <span className="text-slate-400">—</span>,
  },
  {
    header: '인용 URL',
    id: 'urls',
    cell: ({ row }) => (
      <div className="space-y-0.5">
        {row.original.urls.map((u) => (
          <a
            key={u}
            href={u}
            target="_blank"
            rel="noreferrer"
            title={u}
            onClick={(e) => e.stopPropagation()} // 행 클릭(샘플 상세)과 갈라 둔다
            className="block max-w-xs truncate text-xs text-blue-700 hover:underline"
          >
            {shortUrl(u)}
          </a>
        ))}
      </div>
    ),
  },
];

type Tab = 'queries' | 'samples';
const TABS: { id: Tab; label: string }[] = [
  { id: 'queries', label: '질문별' },
  { id: 'samples', label: '샘플' },
];

/**
 * 고른 사이트가 인용된 질문 — 오른쪽 패널에 연다. 두 보기가 **같은 판정·같은 창**이다:
 * 「질문별」은 묶은 통계(행 → 질문 상세), 「샘플」은 펼친 목록(행 → 샘플 상세, 질문으로 거른다).
 * 샘플 목록은 탭을 처음 열 때 받는다
 */
function SiteQueries({ site, onClose }: { site: SiteRow; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('queries');
  const [queryFilter, setQueryFilter] = useState('');
  const [samplesOpened, setSamplesOpened] = useState(false);
  const engine = useEngine();
  const queries = useApi(metrics.siteQueries, { params: { id: site.id }, query: { days: DAYS, engine } });
  const samples = useApi(
    metrics.siteSamples,
    { params: { id: site.id }, query: { days: DAYS, engine } },
    { enabled: samplesOpened },
  );
  const open = (t: Tab) => {
    if (t === 'samples') setSamplesOpened(true);
    setTab(t);
  };
  const all = samples.data?.samples ?? [];
  const shown = queryFilter ? all.filter((x) => x.queryId === queryFilter) : all;
  const res = tab === 'queries' ? queries : samples;

  return (
    <SidePanel
      open
      onClose={onClose}
      title={`인용된 질문 — ${site.name}`}
      description={`${site.urlPrefix} · ${DAYS}일${queries.data ? ` · 질문 ${queries.data.queries.length}개 · 샘플 ${site.cited.k}개` : ''}`}
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-2">
        <div role="tablist" className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => open(t.id)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm',
                tab === t.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === 'samples' ? (
          <Select
            className="w-auto min-w-0 max-w-sm flex-1"
            value={queryFilter}
            onChange={(e) => setQueryFilter(e.target.value)}
          >
            <option value="">전체 질문 ({all.length})</option>
            {queries.data?.queries.map((q) => (
              <option key={q.queryId} value={q.queryId}>
                {q.query} · {q.brandName} ({q.cited.k})
              </option>
            ))}
          </Select>
        ) : null}
      </div>
      {res.isLoading ? (
        <Loading />
      ) : res.error ? (
        <ErrorBox error={res.error} />
      ) : tab === 'queries' ? (
        <DataTable
          columns={siteQueryColumns((id) => {
            setQueryFilter(id);
            open('samples');
          })}
          data={queries.data?.queries ?? []}
          empty="이 기간에 이 사이트를 인용한 질문이 없습니다"
          rowLink={(r) => ({ to: PATHS.admin.query, params: { id: r.queryId } })}
        />
      ) : (
        <DataTable
          columns={siteSampleColumns}
          data={shown}
          empty="이 기간에 이 사이트를 인용한 샘플이 없습니다"
          rowLink={(r) => ({ to: PATHS.admin.sample, params: { id: r.id } })}
        />
      )}
    </SidePanel>
  );
}
const KIND_LABEL = { homepage: '고객 홈페이지', ranking: '순위 사이트', other: '기타' } as const;

/**
 * 「실제 브랜드 | 테스트 | 삭제된 사이트」 한 줄 — 주소의 `?kind=` 가 정한다(brands.tsx 의 「삭제된 브랜드」와 같은 방식).
 * 실제·테스트는 인용률 분모(적격 샘플)를 고르고(261023-test-brand), 삭제된 사이트는 계약 종료로 철거한 홈페이지를
 * 포함한다(261026-retired-site-delete-from-registry). `useBrandKind()` 는 `deleted` 를 실제로 읽는다
 */
const SITES_TABS = [
  { id: 'real', label: '실제 브랜드' },
  { id: 'test', label: '테스트' },
  { id: 'deleted', label: '삭제된 사이트' },
] as const;

export function SitesPage() {
  const navigate = useNavigate();
  const { kind } = useSearch({ strict: false }) as { kind?: string };
  const current = kind === 'test' || kind === 'deleted' ? kind : 'real';
  return (
    <>
      <PageTitle
        title="우리 사이트"
        description="우리가 운영하는 사이트입니다. 운영자에게만 보이고 고객·대행사 화면에는 나오지 않습니다"
      />
      <div className="mb-4 flex flex-wrap items-center gap-4">
        {current !== 'deleted' ? (
          <>
            <EngineTabs />
            <span className="h-5 w-px bg-slate-200" aria-hidden />
          </>
        ) : null}
        <Tabs
          tabs={SITES_TABS}
          value={current}
          // 엔진 탭(EngineTabs)은 유지한다
          onChange={(t) =>
            void navigate({
              to: '.',
              search: ((prev: { engine?: string }) => ({
                ...(prev.engine ? { engine: prev.engine } : {}),
                ...(t === 'real' ? {} : { kind: t }),
              })) as never,
            })
          }
        />
      </div>
      {current === 'deleted' ? <DeletedSites /> : <LiveSites />}
    </>
  );
}

function LiveSites() {
  // 「실제 | 테스트」 탭 — 인용률의 분모(적격 샘플)를 탭이 고른다(261023-test-brand)
  const brandKind = useBrandKind();
  const engine = useEngine();
  const siteMetrics = useApi(metrics.siteMetrics, { query: { days: DAYS, brandKind, engine } });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const create = useApiMutation(registry.createSite);
  const del = useApiMutation(registry.deleteSite);
  const update = useApiMutation(registry.updateSite);
  const [error, setError] = useState<string | null>(null);
  /* 종류 필터 — 30행쯤이라 **클라이언트에서** 거른다. 서버 쿼리를 늘리면 느린 쪽(url_matches)을
     한 번 더 돌게 되고, 전체 건수를 함께 보여 주려면 어차피 전부 받아야 한다. */
  const [kind, setKind] = useState<string>('all');

  const columns: ColumnDef<SiteRow, unknown>[] = [
    { header: 'URL 접두사', accessorKey: 'urlPrefix' },
    { header: '이름', accessorKey: 'name' },
    {
      header: '종류',
      accessorKey: 'kind',
      cell: ({ row }) => (row.original.kind ? KIND_LABEL[row.original.kind] : '—'),
    },
    {
      // 크롤 대조(site:missing-zone)에서 뺄 사이트 — 가비아 DNS 처럼 우리 CF 계정 밖(261026-site-outside-cf)
      header: 'CF 밖',
      id: 'outsideCf',
      accessorKey: 'outsideCf',
      cell: ({ row }) => (
        <input
          type="checkbox"
          aria-label="우리 Cloudflare 계정 밖에서 DNS 를 관리하는 사이트"
          title="우리 Cloudflare 계정 밖에서 DNS 를 관리하는 사이트 — 크롤 도달 경고에서 뺍니다"
          checked={row.original.outsideCf}
          disabled={update.isPending}
          onClick={(e) => e.stopPropagation()} // 행 클릭(인용된 질문 패널)과 갈라 둔다
          onChange={(e) => update.mutate({ params: { id: row.original.id }, body: { outsideCf: e.target.checked } })}
        />
      ),
    },
    {
      header: '인용률 (30일, 전체 적격 샘플)',
      id: 'cited',
      accessorFn: (r) => r.cited.k,
      cell: ({ row }) => <RateCell rate={row.original.cited} />,
    },
    {
      header: '',
      id: 'actions',
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation(); // 행 클릭(인용된 질문 패널)과 갈라 둔다
            if (
              confirm(
                '목록에서 뺍니다. 과거 인용도 조회 시 "우리 사이트"가 아니게 됩니다. 「삭제된 사이트」 탭에서 복구할 수 있습니다',
              )
            )
              del.mutate({ params: { id: row.original.id } });
          }}
        >
          삭제
        </Button>
      ),
    },
  ];

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const form = e.currentTarget;
    const f = new FormData(form);
    try {
      await create.mutateAsync({
        body: {
          urlPrefix: String(f.get('urlPrefix')),
          name: String(f.get('name')),
          kind: (String(f.get('kind')) || null) as SiteRow['kind'],
          outsideCf: f.get('outsideCf') === 'on',
        },
      });
      form.reset();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const all = siteMetrics.data?.sites ?? [];
  // ★ 'none' 은 kind 가 null 인 행이다 — enum 값과 섞이지 않게 따로 가른다
  const rows = kind === 'all' ? all : kind === 'none' ? all.filter((x) => !x.kind) : all.filter((x) => x.kind === kind);
  const selected = all.find((x) => x.id === selectedId) ?? null;

  return (
    <>
      <Card>
        <CardHeader
          title="등록"
          description="host 만 쓰면 www·하위 도메인까지, 경로를 쓰면 그 경로 아래만 잡습니다 (blog.naver.com/ourblog)"
        />
        <CardBody>
          <form className="flex flex-wrap gap-2" onSubmit={submit}>
            <Input name="urlPrefix" className="w-72" placeholder="hnbon.co.kr 또는 blog.naver.com/ourblog" required />
            <Input name="name" className="w-48" placeholder="이름" required />
            <Select name="kind" className="w-40" defaultValue="">
              <option value="">종류 없음</option>
              {SITE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </Select>
            <label className="flex items-center gap-1.5 text-sm text-slate-600">
              <input type="checkbox" name="outsideCf" />
              CF 계정 밖
            </label>
            <Button type="submit">추가</Button>
            <FieldError>{error}</FieldError>
          </form>
        </CardBody>
      </Card>
      <Card>
        <CardHeader
          title="목록"
          description={`${kind === 'all' ? `${all.length}곳` : `${rows.length}곳 (전체 ${all.length}곳)`} · 행을 누르면 인용된 질문을 봅니다`}
          actions={
            <Select className="w-40" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="all">전체 종류</option>
              {SITE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
              <option value="none">종류 없음</option>
            </Select>
          }
        />
        {siteMetrics.isLoading ? (
          <Loading />
        ) : siteMetrics.error ? (
          <ErrorBox error={siteMetrics.error} />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            empty={kind === 'all' ? '등록된 사이트가 없습니다' : '이 종류의 사이트가 없습니다'}
            onRowClick={(r) => setSelectedId(r.id)}
          />
        )}
      </Card>
      {selected ? <SiteQueries key={selected.id} site={selected} onClose={() => setSelectedId(null)} /> : null}
    </>
  );
}

/** 삭제된 사이트 — 최근에 지운 것이 먼저. 복구하면 「우리 사이트」로 돌아가고 과거 인용도 다시 「우리 사이트」로 셉니다 */
function DeletedSites() {
  const list = useApi(registry.listDeletedSites);
  const restore = useApiMutation(registry.restoreSite);
  const columns: ColumnDef<DeletedSiteRow, unknown>[] = [
    { header: 'URL 접두사', accessorKey: 'urlPrefix' },
    { header: '이름', accessorKey: 'name' },
    {
      header: '종류',
      accessorKey: 'kind',
      cell: ({ row }) => (row.original.kind ? KIND_LABEL[row.original.kind] : '—'),
    },
    { header: '삭제한 때', id: 'deletedAt', cell: ({ row }) => dateTime(row.original.deletedAt) },
    {
      header: '',
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            disabled={restore.isPending}
            onClick={() => {
              if (
                confirm(
                  `${row.original.urlPrefix} 을(를) 복구합니다. 「우리 사이트」로 돌아가고 과거 인용도 다시 "우리 사이트"로 셉니다. 계속할까요?`,
                )
              )
                restore.mutate({ params: { id: row.original.id } });
            }}
          >
            복구
          </Button>
        </div>
      ),
    },
  ];
  return (
    <Card>
      <CardHeader
        title="삭제된 사이트"
        description="계약 종료로 철거한 홈페이지를 포함합니다. 최근에 지운 것이 먼저입니다"
      />
      {restore.error ? <ErrorBox error={restore.error} /> : null}
      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : (
        <DataTable columns={columns} data={list.data?.sites ?? []} empty="삭제된 사이트가 없습니다" />
      )}
    </Card>
  );
}
